import { FC, useEffect, useMemo } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { MiBuildHouseAlt } from "@miplatform/assets";
import { MiAlert, MiPlaceholderArea } from "@miplatform/components";
import {
  ButtonVariants,
  CalendarEventType,
  Colors,
  IOrderTemplateResponse,
} from "@miplatform/types";
import { getDate, minutesToHours } from "@miplatform/utils";
import { mdiPlus } from "@mdi/js";
import { Box, Typography } from "@mui/material";
import { ConfigAccordionHeader } from "../../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import { CALENDAR_PAGE_CONFIG } from "../../ConfigCalendarSetList.constants";
import { ICalendarSetData } from "../../ConfigCalendarSetList.types";
import { CalendarWorkCapacityBoardPreview } from "./CalendarWorkCapacityBoardPreview";
import { CalendarWorkCapacityCard } from "./CalendarWorkCapacityCard";

export const CalendarWorkCapacityTabView: FC<
  CalendarWorkCapacityTabViewProps
> = ({
  form,
  extraTabProps: {
    isReadOnly,
    id,
    eventType = CalendarEventType.Order,
    orderTemplates = [],
    onAddWorkTemplateBase,
  } = {},
}: CalendarWorkCapacityTabViewProps) => {
  const { classes, theme } = useStyles();
  const { control, setValue, getValues, watch } = form;
  const { dayStartTime, dayEndTime, assignableUsers } = watch();
  const workTemplateBases = useWatch({
    control,
    name: "workTemplateBases",
  });

  const workCapacities = useWatch({
    control,
    name: "workCapacities",
  });
  const index = assignableUsers.findIndex((user) => user.id === id) || 0;
  const selectedUser = assignableUsers[index];
  const selectedUserWorkCapacities = workCapacities[selectedUser?.id] || {};
  const sortedWorkTemplateBases = useMemo(
    () =>
      [...workTemplateBases].sort(
        (a, b) =>
          (selectedUserWorkCapacities[a.id]?.displayOrder || 0) -
          (selectedUserWorkCapacities[b.id]?.displayOrder || 0)
      ),
    [workTemplateBases, workCapacities]
  );

  const templatesDurations = useMemo(
    () =>
      orderTemplates.reduce((acc, { id, expectedDurationInMinutes }) => {
        acc[id] = minutesToHours(expectedDurationInMinutes) || 1;

        return acc;
      }, {} as Record<string, number>),
    [orderTemplates]
  );

  const { isIncrementDisabled, totalCapacity, wholeHours } = useMemo(() => {
    const totalCapacity = Object.values(selectedUserWorkCapacities).reduce(
      (sum, capacity) =>
        sum +
        (capacity.amount || 0) *
          (templatesDurations[capacity.orderTemplateId] || 1),
      0
    );
    const wholeHours = getDate(dayEndTime).diff(dayStartTime, "hours");

    return {
      isIncrementDisabled: totalCapacity >= wholeHours,
      totalCapacity,
      wholeHours,
    };
  }, [
    selectedUserWorkCapacities,
    dayStartTime,
    dayEndTime,
    templatesDurations,
  ]);

  const swapDisplayOrders = (aIndex: number, bIndex: number) => {
    const newWorkCapacities = { ...selectedUserWorkCapacities };
    const orderTemplateA = sortedWorkTemplateBases[aIndex];
    const orderTemplateB = sortedWorkTemplateBases[bIndex];
    const tempDisplayOrder = newWorkCapacities[orderTemplateA.id].displayOrder;
    newWorkCapacities[orderTemplateA.id].displayOrder =
      newWorkCapacities[orderTemplateB.id].displayOrder;
    newWorkCapacities[orderTemplateB.id].displayOrder = tempDisplayOrder;

    return newWorkCapacities;
  };

  const handleReorderUp = (orderTemplateId: string) => {
    const index = sortedWorkTemplateBases.findIndex(
      (template) => template.id === orderTemplateId
    );

    if (index !== -1 && index > 0) {
      const newWorkCapacities = swapDisplayOrders(index, index - 1);

      setValue(
        "workCapacities",
        {
          ...workCapacities,
          [selectedUser.id]: newWorkCapacities,
        },
        { shouldDirty: true }
      );
    }
  };

  const handleReorderDown = (orderTemplateId: string) => {
    const index = sortedWorkTemplateBases.findIndex(
      (template) => template.id === orderTemplateId
    );

    if (index !== -1 && index < sortedWorkTemplateBases.length - 1) {
      const newWorkCapacities = swapDisplayOrders(index, index + 1);

      setValue(
        "workCapacities",
        {
          ...workCapacities,
          [selectedUser.id]: newWorkCapacities,
        },
        { shouldDirty: true }
      );
    }
  };

  const handleQuantityChange = (orderTemplateId: string, value: number) => {
    const newWorkCapacities = {
      ...selectedUserWorkCapacities,
      [orderTemplateId]: {
        ...selectedUserWorkCapacities[orderTemplateId],
        amount: value,
      },
    };

    setValue(
      "workCapacities",
      {
        ...getValues("workCapacities"),
        [selectedUser.id]: newWorkCapacities,
      },
      { shouldDirty: true }
    );
  };

  useEffect(() => {
    if (selectedUser) {
      workTemplateBases
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .forEach((template, index) => {
          if (!selectedUserWorkCapacities[template.id]) {
            selectedUserWorkCapacities[template.id] = {
              amount: 0,
              orderTemplateId: template.id,
              displayOrder: index + 1,
            };
          }
        });
      setValue("workCapacities", {
        ...getValues("workCapacities"),
        [selectedUser.id]: selectedUserWorkCapacities,
      });
    }
  }, [assignableUsers, index, workTemplateBases]);

  return (
    <Box className={classes.root}>
      <Box flex={1} className={classes.item}>
        <ConfigAccordionHeader
          title={`${selectedUser?.name} - Daily Work Capacity`}
        >
          {workTemplateBases.length === 0 ? (
            <MiPlaceholderArea
              text={`No Attached ${CALENDAR_PAGE_CONFIG[eventType].entityName} Types`}
              helperText={`Tip: attach ${CALENDAR_PAGE_CONFIG[
                eventType
              ].entityName.toLowerCase()} types to see them here.`}
              iconComponent={<img src={MiBuildHouseAlt} alt="House Icon" />}
              containerStyle={{
                backgroundColor: theme.palette.primary.light,
                height: "100%",
                minHeight: 450,
              }}
              buttons={
                !isReadOnly
                  ? [
                      {
                        title: `Add ${CALENDAR_PAGE_CONFIG[eventType].entityName} Types`,
                        startIcon: mdiPlus,
                        variant: ButtonVariants.Contained,
                        onClick: onAddWorkTemplateBase,
                      },
                    ]
                  : []
              }
            />
          ) : (
            <>
              <Box className={classes.orderTemplatesContainer}>
                <Typography
                  fontWeight={theme.fontWeight.semiBold}
                  color={theme.palette.primary.main}
                >
                  Set Duration Per Day
                </Typography>
                {sortedWorkTemplateBases.map((template, index) => (
                  <CalendarWorkCapacityCard
                    key={template.id}
                    orderTemplate={template}
                    config={selectedUserWorkCapacities[template.id]}
                    isFirstInList={index === 0}
                    isLastInList={index === sortedWorkTemplateBases.length - 1}
                    isReadOnly={isReadOnly}
                    isIncrementDisabled={
                      (templatesDurations[template.id] || 1) >
                      wholeHours - totalCapacity
                    }
                    onQuantityChange={handleQuantityChange}
                    onReorderUp={handleReorderUp}
                    onReorderDown={handleReorderDown}
                  />
                ))}
              </Box>
              <MiAlert
                description="Set the daily work capacity for attached work to your user."
                sx={{ mt: 2, py: 0, px: 2 }}
              />
              <MiAlert
                isOpen={isIncrementDisabled}
                variant="warning"
                description="You have maxed out the work capacity for the configured day start time and day end time."
                sx={{ mt: 2, py: 0, px: 2 }}
              />
            </>
          )}
        </ConfigAccordionHeader>
      </Box>
      {workTemplateBases.length > 0 && (
        <Box width={254} className={classes.item}>
          <ConfigAccordionHeader title="Board Preview" color={Colors.Secondary}>
            <CalendarWorkCapacityBoardPreview
              startTime={dayStartTime as string}
              endTime={dayEndTime as string}
              durations={templatesDurations}
              workCapacities={Object.values(selectedUserWorkCapacities)}
              userName={selectedUser?.name}
              workTemplateBases={workTemplateBases}
            />
          </ConfigAccordionHeader>
        </Box>
      )}
    </Box>
  );
};

interface CalendarWorkCapacityTabViewProps {
  form: UseFormReturn<ICalendarSetData>;
  extraTabProps?: {
    isReadOnly?: boolean;
    id?: string;
    eventType?: CalendarEventType;
    orderTemplates?: IOrderTemplateResponse[];
    onAddWorkTemplateBase?: () => void;
  };
}

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    gap: theme.spacing(3),
    width: "100%",
    paddingBottom: theme.spacing(3),
  },
  item: {
    display: "flex",
    flexDirection: "column",
  },
  orderTemplatesContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    borderRadius: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey.mediumLight,
  },
}));
